import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useCountry } from '../../hooks/useCountry';
import { useCountryCities } from '../../hooks/useCity';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEditAttendee, useGetEventAttendees } from '../../hooks/useAttendee';
import { useEditDoctorsMasterData, useDoctorMasterDataById } from '../../hooks/useDoctorMasterData';
import { useEditEvent, useGetEventDataById } from '../../hooks/useEvent';
import { getEventStatus } from './EventStatus';

function UpdateSubscribersAttendEvent({ openEditModal, setOpenEditModal, selectedSubscriber }) {
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState({
    national_id: '',
    first_name: '',
    family_name: '',
    email: '',
    phone_number: '',
    country_id: "",
    city_id: "",
    organization: '',
    specialty: '',
    APO_number: '',
    medical_license: '',
    signature: "No Signature",
    industry_area: '',
    doctor_master_data_id: '',
    created_at: null,
    updated_at: null,
  });

  // Update form values when selectedSubscriber changes
  useEffect(() => {
    if (selectedSubscriber) {
      setValues({
        national_id: selectedSubscriber.national_id || '',
        first_name: selectedSubscriber.first_name || '',
        family_name: selectedSubscriber.family_name || '',
        email: selectedSubscriber.email || '',
        phone_number: selectedSubscriber.phone_number || '',
        country_id: selectedSubscriber.country_id || '',
        city_id: selectedSubscriber.city_id || '',
        organization: selectedSubscriber.organization || '',
        specialty: selectedSubscriber.specialty || '',
        APO_number: selectedSubscriber.APO_number || '',
        medical_license: selectedSubscriber.medical_license || '',
        signature: selectedSubscriber.signature || "No Signature",
        industry_area: selectedSubscriber.industry_area || '',
        doctor_master_data_id: selectedSubscriber.doctor_master_data_id || '',
        updated_at: selectedSubscriber.updated_at || null,
      });
    }
  }, [selectedSubscriber]);

  const { data: countryData } = useCountry();
  const { data: filteredCities } = useCountryCities(values?.country_id);
  const { mutate: editAttendee } = useEditAttendee();
  const { mutateAsync: editDoctorMasterData } = useEditDoctorsMasterData();
  const { data: doctorMasterData } = useDoctorMasterDataById(values.doctor_master_data_id);
  const { data: attendeesData } = useGetEventAttendees(selectedSubscriber?.event_id);
  const { data: eventData } = useGetEventDataById(selectedSubscriber?.event_id);
  const { mutate: editEvent } = useEditEvent();


  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues(prev => ({
      ...prev,
      [name]: value,
    }));
    setErrors(prev => ({
      ...prev,
      [name]: '',
    }));
  };

  const validationRules = {
    national_id: {
      required: 'National ID is required',
      pattern: 'National ID must be a number',
    },
    first_name: {
      required: 'First Name is required',
      minLength: 'First Name must be at least 3 characters',
    },
    family_name: {
      required: 'Family Name is required',
      minLength: 'Family Name must be at least 3 characters',
    },
    email: {
      required: 'Email is required',
      pattern: 'Email must be a valid email address',
    },
    phone_number: {
      required: 'Phone Number is required',
      pattern: 'Phone Number must be a number',
    },
    country_id: {
      required: 'Country is required',
    },
    city_id: {
      required: 'City is required',
    },
    specialty: {
      required: 'Specialty is required',
    },
    medical_license: {
      required: 'Medical License is required',
    }
  };

  const validateForm = (formData) => {
    const newErrors = {};

    for (const field in validationRules) {
      const value = formData[field] || '';
      const rules = validationRules[field];

      if (rules.required && !value.trim()) {
        newErrors[field] = rules.required;
        continue;
      }

      if (rules.minLength && value.length < (rules.minLength.match(/\d+/) || [])[0]) {
        newErrors[field] = rules.minLength;
        continue;
      }

      if (rules.maxLength && value.length > (rules.maxLength.match(/\d+/) || [])[0]) {
        newErrors[field] = rules.maxLength;
        continue;
      }

      if (rules.pattern) {
        let regex;
        if (field === 'email') {
          regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        } else if (field === 'national_id') {
          regex = /^\d+$/;
        }
        if (regex && !regex.test(value)) {
          newErrors[field] = rules.pattern;
        }
      }
    }
    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const validationErrors = validateForm(values);

      if (Object.keys(validationErrors).length > 0) {
        toast.error('Please fill all required fields correctly');
        return;
      }

      // If we have a doctor master data ID, check for changes and update if needed
      if (values.doctor_master_data_id && doctorMasterData) {
        const hasChanges =
          doctorMasterData.APO_number !== values.APO_number ||
          doctorMasterData.industry_area !== values.industry_area;

        if (hasChanges) {
          try {
            const updatedDoctorData = {
              APO_number: values.APO_number,
              industry_area: values.industry_area,
              updated_at: new Date().toISOString(),
            };
            await editDoctorMasterData({
              id: values.doctor_master_data_id,
              data: updatedDoctorData
            });
            toast.info('Doctor master data updated with new information');
          } catch (error) {
            toast.error('Failed to update doctor master data');
            return;
          }
        }
      }

      // Update the attendee
      editAttendee({
        id: selectedSubscriber.id,
        data: {
          ...values,
          updated_at: new Date().toISOString()
        }
      });
      const currentStatus = getEventStatus(eventData.start_date, eventData.end_date, attendeesData, eventData.spend_value, eventData.organization_id, eventData.tov_exported);
      if (currentStatus.text !== eventData.status) {
        editEvent({ id: eventData.id, data: { status: currentStatus.text } });
      }
      toast.success('Attendee updated successfully');
      setOpenEditModal(false);

    } catch (error) {
      toast.error('Failed to update attendee');
    }
  };

  return (
    <div>
      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          },
          '& .MuiPaper-root': {
            boxShadow: 'none',
          }
        }}
      >
        <DialogTitle className="bg-blueColor text-white py-4">
          Edit Attendee
        </DialogTitle>
        <DialogContent className="mt-4">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
              <div className='col-span-2'>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  National ID <span className="text-red-500">*</span>
                </label>
                <input
                  name='national_id'
                  value={values.national_id}
                  type="tel"
                  disabled
                  className="w-full p-2 border rounded bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  name='first_name'
                  value={values.first_name}
                  type="text"
                  disabled
                  className="w-full p-2 border rounded bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Family Name <span className="text-red-500">*</span>
                </label>
                <input
                  name='family_name'
                  value={values.family_name}
                  type="text"
                  disabled
                  className="w-full p-2 border rounded bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  name='email'
                  value={values.email}
                  type="email"
                  disabled
                  className="w-full p-2 border rounded bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value="+966"
                    disabled
                    className="w-16 p-2 border rounded-l bg-gray-100"
                  />
                  <input
                    name='phone_number'
                    value={values.phone_number}
                    type="text"
                    disabled
                    className="w-full p-2 border rounded-r bg-gray-100"
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  name='country_id'
                  value={values.country_id}
                  disabled
                  className="w-full p-2 border rounded bg-gray-100"
                >
                  <option value="" hidden>Select Country</option>
                  {countryData?.map((country) => (
                    <option key={country.id} value={country.id}>{country.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <select
                  name='city_id'
                  value={values.city_id}
                  disabled
                  className="w-full p-2 border rounded bg-gray-100"
                >
                  <option value="" hidden>Select City</option>
                  {filteredCities?.map((city) => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Organization
                </label>
                <input
                  name='organization'
                  value={values.organization}
                  type="text"
                  disabled
                  className="w-full p-2 border rounded bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Specialty <span className="text-red-500">*</span>
                </label>
                <input
                  name='specialty'
                  value={values.specialty}
                  type="text"
                  disabled
                  className="w-full p-2 border rounded bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Medical License <span className="text-red-500">*</span>
                </label>
                <input
                  name='medical_license'
                  value={values.medical_license}
                  type="text"
                  disabled
                  className="w-full p-2 border rounded bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Practice/Sector
                </label>
                <select
                  name='industry_area'
                  onChange={handleChange}
                  value={values.industry_area}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                >
                  <option value="" hidden>Select Practice/Sector</option>
                  <option value="private">Private</option>
                  <option value="public">Public</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  APO Number <span className="text-red-500">*</span>
                </label>
                <input
                  name='APO_number'
                  onChange={handleChange}
                  value={values.APO_number}
                  type="text"
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                />
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-6 p-4">
              <button
                type="button"
                onClick={() => setOpenEditModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                data-track="edit-attendee-button"
                data-module="Attendees"
                type="submit"
                className="px-4 py-2 bg-blueColor text-white rounded"
              >
                Save Changes
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UpdateSubscribersAttendEvent;